import { cleanDomain } from "../../s3";

const URN_PREFIX = "urn:autoforce";

/**
 * Identificadores estáveis do grafo. São a cola entre os nós: o mesmo
 * `dealerId` aparece no `subOrganization` da home e no nó completo da página da
 * unidade, e o `organizationId` é referenciado pelo `parentOrganization` de
 * cada `AutoDealer`.
 *
 * São URNs, não URLs: o `@id` não precisa endereçar nada, precisa identificar.
 * Um `@id` que não é IRI absoluto o JSON-LD resolve relativo à URL da página, e
 * o mesmo nó ganharia identificadores diferentes em páginas diferentes. O
 * sufixo separa esquemas que compartilham o mesmo ID público — o do canal serve
 * tanto à `Organization` quanto ao `WebSite`.
 */
export function organizationId(channelPublicId: string): string {
  return `${URN_PREFIX}:channel:${channelPublicId}#organization`;
}

export function websiteId(channelPublicId: string): string {
  return `${URN_PREFIX}:channel:${channelPublicId}#website`;
}

export function dealerId(unitPublicId: string): string {
  return `${URN_PREFIX}:unit:${unitPublicId}#dealer`;
}

/**
 * Identidade de um registro do CMS. O sufixo é **constante**: derivá-lo do
 * `@type` configurado faria a identidade da entidade mudar quando o racer
 * trocasse a seleção, e o critério pede identificador estável. O mesmo id sai na
 * listagem e na página de detalhe, e o `mergeById` consolida as duas ocorrências
 * quando o mesmo registro aparece em dois blocos da mesma página.
 */
export function entryId(contentEntryId: string): string {
  return `${URN_PREFIX}:entry:${contentEntryId}#content`;
}

/**
 * Identidade de uma trilha de navegação: a URL canônica da página mais o bloco
 * que a declara.
 *
 * A página entra porque a trilha é **dela**: um bloco vindo de template aparece
 * em muitas páginas, e cada uma tem a sua. Por isso aqui não se usa URN como nos
 * demais helpers deste arquivo — URN vale igual em qualquer documento, o que
 * descreve bem `WebSite`, `Organization` e `AutoDealer`, que são a mesma
 * entidade em toda página.
 *
 * O bloco entra porque a mesma página pode ter mais de uma trilha, e trilha é
 * lista posicional: sob um `@id` só, o `mergeById` uniria os `itemListElement`
 * numa lista com `position` repetida.
 *
 * O canal e o bloco são a base quando a canônica não chega, para o nó nunca
 * ficar sem identidade.
 */
export function breadcrumbListId({
  canonicalUrl,
  channelPublicId,
  nodeId,
  variant,
}: {
  canonicalUrl?: string | null;
  channelPublicId: string;
  nodeId: string;
  variant: "static" | "dynamic";
}): string {
  const page = canonicalUrl?.trim();
  const suffix = `#breadcrumb-${variant}-${nodeId}`;

  if (page) return `${page}${suffix}`;

  return `${URN_PREFIX}:channel:${channelPublicId}${suffix}`;
}

/**
 * `@id` e `url` canônicos do grupo controlador, derivados da URL oficial
 * informada no canal. Aqui a URL é a base certa: o grupo é entidade externa,
 * não tem ID público nosso, e sites diferentes da mesma conta que apontem para
 * a mesma URL convergem para o mesmo identificador.
 */
export function parentOrganizationIdentity(
  parentUrl: string,
): { id: string; url: string } | null {
  // Sem a canonicalização apex→www do `buildSiteOrigin`: o grupo pode ser de
  // terceiro (supor a infra dele seria chute) ou nosso (o apex já redireciona).
  const domain = cleanDomain(parentUrl);
  if (!domain) return null;

  const origin = `https://${domain}`;

  return { id: `${origin}/#organization`, url: `${origin}/` };
}
